import * as React from "react";
import {useEffect, useState} from "react";
import {useTheme} from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import {Card, TextField, Grid, Alert, CircularProgress} from "@mui/material";
import {GET, PATCH} from "APIclient";
import properties from "constants/ApplicationConstants";
import MDButton from "components/MDButton";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {useNavigate, useParams} from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import {formatDesignation} from "constants/ApplicationConstants";

export default function ExtendPerformanceImprovement() {
  const theme = useTheme();
  const {empId} = useParams();
  const navigate = useNavigate();
  const [searchResult, setSearchResult] = useState({
    employeeId: "",
    firstName: "",
    middleName: "",
    lastName: "",
    pipStartDate: "",
    pipEndDate: "",
    designation: "",
    personalEmail: "",
    officialEmail: "",
    empPipId: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);

  const handleClose = () => {
    setOpen(false);
  };

  const [pipData, setPipData] = useState({
    newEndDate: "",
    reason: ""
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  const handleBack = () => {
    setSearchResult({
      employeeId: "",
      firstName: "",
      middleName: "",
      lastName: "",
      pipStartDate: "",
      pipEndDate: "",
      designation: "",
      personalEmail: "",
      officialEmail: "",
      empPipId: null
    });
    setError(null);
    setPipData({
      newEndDate: "",
      reason: ""
    });
    setSaveError(null);
    setFormErrors({});
    setIsSaveDisabled(true);
    navigate("/performanceImprovementList");
  };

  const formatDate = dateString => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "newEndDate":
        if (!value) {
          error = "New End Date is required";
        } else if (dayjs(value).isBefore(dayjs(searchResult.pipEndDate, "DD MMM YYYY"), "day")) {
          error = "New End Date must be after the current PIP End Date";
        }
        break;
      case "reason":
        if (!value) {
          error = "Reason is required";
        } else if (!/^(?![\s.]+$)[A-Za-z0-9\s.,'-:;()]+$/.test(value)) {
          error = "Reason can contain letters.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  const handlePipChange = field => event => {
    const value = event.target ? event.target.value : event;
    setPipData(prev => ({...prev, [field]: value}));

    const error = validateField(field, value);
    setFormErrors(prev => ({...prev, [field]: error}));
  };

  const handleSave = async () => {
    const newErrors = {};
    Object.keys(pipData).forEach(key => {
      const error = validateField(key, pipData[key]);
      if (error) newErrors[key] = error;
    });

    setFormErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    setSaveLoading(true);
    setSaveError(null);

    const payload = {
      empId: parseInt(searchResult.employeeId),
      empPipId: searchResult.empPipId,
      pipEndDate: pipData.newEndDate,
      reason: pipData.reason
    };

    try {
      const url = "admin/extend-pip";
      const response = await PATCH(url, payload);
      if (response?.status === 200) {
        setSnakColor("success");
        setSnakMessage("Performance Improvement Program extended successfully.");
        setOpen(true);
        setTimeout(() => {
          navigate("/performanceImprovementList");
        }, 1500);
      }
    } catch (error) {
      const statusCode = error?.response?.status;
      const message =
        error?.response?.data?.message || "Failed to extend Performance Improvement Program.";

      if (statusCode === 400) {
        setSnakColor("error");
        setSnakMessage("Bad request. Please check your inputs.");
      } else if (statusCode === 401) {
        setSnakColor("error");
        setSnakMessage("Unauthorized access. Please log in again.");
      } else if (statusCode === 403) {
        setSnakColor("error");
        setSnakMessage("Forbidden. You don’t have permission to perform this action.");
      } else if (statusCode === 404) {
        setSnakColor("error");
        setSnakMessage("API not found. Please contact support.");
      } else if (statusCode === 409) {
        setSnakColor("error");
        setSnakMessage("Conflict. Please check your inputs.");
      } else if (statusCode === 500) {
        setSnakColor("error");
        setSnakMessage("Server error. Please try again later.");
      } else {
        setSnakColor("error");
        setSnakMessage(message);
      }
      setOpen(true);
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    const allFieldsFilled = Object.keys(pipData).every(key => pipData[key] !== "");
    const noErrors = Object.keys(formErrors).every(key => !formErrors[key]);
    setIsSaveDisabled(!(allFieldsFilled && noErrors));
  }, [pipData, formErrors]);

  useEffect(() => {
    const fetchPipData = async () => {
      if (!empId) {
        setError("No Employee ID provided.");
        setSnakColor("error");
        setSnakMessage("No Employee ID provided.");
        setOpen(true);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await GET(`admin/get-pipList?employeeId=${empId}`);
        if (response?.status === 200 && response.data?.data?.paginatedData?.length > 0) {
          const employee = response.data.data.paginatedData[0];
          setSearchResult({
            employeeId: employee.employeeId?.toString() || "",
            firstName: employee.firstName || "",
            middleName: employee.middleName || "",
            lastName: employee.lastName || "",
            pipStartDate: formatDate(employee.pipStartDate),
            pipEndDate: formatDate(employee.pipEndDate),
            designation: formatDesignation(employee.designation),
            personalEmail: employee.emailAddress || "",
            officialEmail: employee.officialEmailAddress || "",
            empPipId: employee.empPipId || null,
            mobileNo: employee.mobileNo || ""
          });
        } else {
          setError("No PIP data found for the given Employee ID.");
          setSnakColor("error");
          setSnakMessage("No PIP data found for the given Employee ID.");
          setOpen(true);
        }
      } catch (error) {
        const statusCode = error?.response?.status;
        const message = error?.response?.data?.message || "Failed to fetch PIP data.";
        if (statusCode === 400) {
          setSnakColor("error");
          setSnakMessage("Bad request. Please check the Employee ID.");
        } else if (statusCode === 401) {
          setSnakColor("error");
          setSnakMessage("Unauthorized access. Please log in again.");
        } else if (statusCode === 403) {
          setSnakColor("error");
          setSnakMessage("Forbidden. You don’t have permission to perform this action.");
        } else if (statusCode === 404) {
          setSnakColor("error");
          setSnakMessage("No PIP data found for the given Employee ID.");
        } else if (statusCode === 500) {
          setSnakColor("error");
          setSnakMessage("Server error. Please try again later.");
        } else {
          setSnakColor("error");
          setSnakMessage(message);
        }
        setOpen(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPipData();
  }, [empId]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <>
          {isLoading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </MDBox>
          ) : (
            <>
              <MDTypography
                variant="h6"
                fontWeight="medium"
                pb={2}
                sx={{fontFamily: properties.fontFamily}}
              >
                Employee Details
              </MDTypography>
              <Card sx={{padding: 3, marginBottom: 3}}>
                {/* Employee ID in a separate row */}
                <MDBox display="flex" alignItems="center" mb={2}>
                  <MDTypography
                    variant="caption"
                    color="text"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                  >
                    Employee ID:
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    fontFamily={properties.fontFamily}
                    sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                  >
                    {searchResult.employeeId || "-"}
                  </MDTypography>
                </MDBox>

                {/* Other fields in grid layout */}
                <MDBox
                  display="grid"
                  gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                  gap={2}
                  mb={2}
                >
                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      First Name:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.firstName || "-"}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      Middle Name:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.middleName || "-"}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      Last Name:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.lastName || "-"}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox
                  display="grid"
                  gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                  gap={2}
                  mb={2}
                >
                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      PIP Start Date:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.pipStartDate || "-"}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      PIP End Date:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.pipEndDate || "-"}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      Designation:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.designation || "-"}
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox
                  display="grid"
                  gridTemplateColumns={{xs: "1fr", sm: "repeat(3, 1fr)"}}
                  gap={2}
                  mb={2}
                >
                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      Personal Email ID:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult?.personalEmail || "-"}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      Official Email ID:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult.officialEmail || "-"}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDTypography
                      variant="caption"
                      color="text"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "500", minWidth: "120px"}}
                    >
                      Mobile:
                    </MDTypography>
                    <MDTypography
                      variant="caption"
                      fontFamily={properties.fontFamily}
                      sx={{fontSize: "12px", fontWeight: "600", minWidth: "120px"}}
                    >
                      {searchResult?.mobileNo || "-"}
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Card>

              <MDTypography
                variant="h6"
                fontWeight="medium"
                sx={{fontFamily: properties.fontFamily}}
                gutterBottom
              >
                Extend Performance Improvement Program
              </MDTypography>

              <Card sx={{padding: 3}}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <MDBox pt={2}>
                    <DatePicker
                      format="DD/MM/YYYY"
                      sx={{m: 1, width: "38ch", paddingBottom: "20"}}
                      size="small"
                      label={
                        <span>
                          PIP Extend Date <span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      name="newEndDate"
                      value={pipData.newEndDate ? dayjs(pipData.newEndDate) : null}
                      onChange={newValue => {
                        const formattedDate = newValue ? dayjs(newValue).format("YYYY-MM-DD") : "";
                        handlePipChange("newEndDate")({target: {value: formattedDate}});
                      }}
                      slotProps={{
                        textField: {
                          error: !!formErrors.newEndDate,
                          helperText: formErrors.newEndDate
                        }
                      }}
                    />
                  </MDBox>
                  <Grid item xs={12} pt={2} ml={1}>
                    <TextField
                      label={
                        <span>
                          Reason <span style={{color: properties.colors.Red}}>*</span>
                        </span>
                      }
                      multiline
                      rows={3}
                      value={pipData.reason}
                      onChange={handlePipChange("reason")}
                      fullWidth
                      sx={{
                        maxWidth: 427,
                        "& .MuiInputBase-root": {minHeight: 80}
                      }}
                      error={!!formErrors.reason}
                      helperText={formErrors.reason}
                    />
                  </Grid>

                  {saveError && (
                    <MDTypography color="error" variant="body2" sx={{marginTop: 2, ml: 1}}>
                      {saveError}
                    </MDTypography>
                  )}

                  <MDBox mt={3} mb={2} sx={{display: "flex", justifyContent: "flex-end"}}>
                    <MDBox sx={{display: "flex", gap: 1}}>
                      <MDButton
                        variant="gradient"
                        color="warning"
                        sx={{width: "20ch", fontFamily: properties.fontFamily}}
                        onClick={handleBack}
                      >
                        Back
                      </MDButton>
                      <MDButton
                        variant="gradient"
                        color="info"
                        sx={{
                          width: "20ch",
                          ml: 1,
                          mr: 1,
                          background: properties.colors.bgcolorbluethem,
                          fontFamily: properties.fontFamily
                        }}
                        onClick={handleSave}
                        disabled={isSaveDisabled || saveLoading}
                      >
                        Save
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </LocalizationProvider>
              </Card>
            </>
          )}
        </>

        <Snackbar
          open={open}
          anchorOrigin={{vertical: "top", horizontal: "right"}}
          autoHideDuration={6000}
          onClose={handleClose}
        >
          <Alert
            onClose={handleClose}
            severity={snakColor === "success" ? "success" : "error"}
            variant="filled"
            sx={{width: "100%", color: "#fff"}}
          >
            {snakMessage}
          </Alert>
        </Snackbar>
      </MDBox>
    </DashboardLayout>
  );
}
